import CloseIcon from '@mui/icons-material/Close';
import * as Mui from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import FileUploader from "../../../../../../util/component/FileUploader";
import { ContextOperation } from "../../../../context/OperationContext";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import OperationTypes from "../../../../types";
import FormInput from "../../../common/FormInput";
import { nameof } from "../../../common/ObjectUtils";
import { CreateDamageFormTypes } from "../../../damageFormList/Types";
import DamagePriceService from "../../service/damagePrice.action";

interface CreateDamageForm {
    partId: number; // ID della parte selezionata
    positionId: number; // ID della posizione della parte selezionata
    severity: string; // Severity selezionata
}

interface Props {
    open: boolean;
    onClose: () => void;
    onCreateDamage: (damageSaved: OperationTypes.Damage) => void;
    parts: OperationTypes.VehiclePart[];
    severities: string[];
    plate: string;
}
const CreateDamage = (props: Props) => {
    const t = useOperationTranslation();
    const { tenant, setLoading } = useContext(ContextOperation);

    const form = useForm<CreateDamageForm>();

    const parts = useMemo(() => {
        return props.parts.map((part) => ({
            label: part.label,
            value: `${part.part.id}.${part.position.id}`,
        }));
    }, [props.parts]);

    const severities = useMemo(() => {
        return props.severities.map((sev) => ({
            label: sev,
            value: sev
        }));
    }, [props.severities]);

    const [fileUploadedOne, setFileUploadedOne] = useState<File | null>(null);
    const [fileUploadedTwo, setFileUploadedTwo] = useState<File | null>(null);

    const onFileUpload = (file: File, photoNumber: number) => {
        if (photoNumber === 1) {
            setFileUploadedOne(file);
        } else if (photoNumber === 2) {
            setFileUploadedTwo(file);
        }
    }

    const onCreateDamage = (respCreateDamage: CreateDamageFormTypes.IRespCreateDamage) => {
        const damage = {
            id: respCreateDamage.id,
            vehicleId: respCreateDamage.vehicleId,
            part: {
                id: respCreateDamage.partId
            },
            position: {
                id: respCreateDamage.partPositionId
            },
            severity: respCreateDamage.severity,
            firstSnapshotId: respCreateDamage.firstPhotoId,
            secondSnapshotId: respCreateDamage.secondPhotoId,
            temporary: respCreateDamage.temporary
        }
        props.onCreateDamage(damage as OperationTypes.Damage);
    }

    const onSubmit = (data: CreateDamageForm) => {
        setLoading(true);

        DamagePriceService.post.addOpen(tenant, {
            damageList: [
                {
                    partId: data.partId,
                    positionId: data.positionId,
                    severity: data.severity,
                    damageDate: new Date().toISOString(),
                },
            ],
            vehiclePlate: props.plate,
        }).then(
            response => {
                if (!fileUploadedOne && !fileUploadedTwo) {
                    onCreateDamage(response[0]);
                    onClose();
                    setLoading(false);
                } else {
                    DamagePriceService.post.addPhoto(response[0].id, [fileUploadedOne, fileUploadedTwo]).then(
                        responsePhoto => {
                            setLoading(false);

                            if (fileUploadedOne) {
                                
                                response[0].firstPhotoId = responsePhoto[0].id;

                                if (fileUploadedTwo) {
                                    response[0].secondPhotoId = responsePhoto[1].id;
                                }

                            } else {
                                if (fileUploadedTwo) {
                                    response[0].secondPhotoId = responsePhoto[0].id;
                                }
                            }
                            
                            onCreateDamage(response[0]);

                            setFileUploadedOne(null);
                            setFileUploadedTwo(null);
    
                            onClose();
                        },
                        error => {
                            setLoading(false);
                        }
                    )
                }
            },
            error => {
                setLoading(false);
            }
        );
    }

    const onClose = () => {
        form.reset();
        props.onClose();
    }
    
    return(
        <Mui.Dialog
            open={props.open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <Mui.DialogTitle>
                <Mui.Grid container alignItems={"center"}>
                    <Mui.Grid item>
                        <Mui.Typography variant="h5">
                            {t("damageList.damageCreation.dialogs.createDamage.title")}
                        </Mui.Typography>
                    </Mui.Grid>
                    <Mui.Grid item marginLeft={"auto"}>
                        <Mui.IconButton onClick={onClose}>
                            <CloseIcon />
                        </Mui.IconButton>
                    </Mui.Grid>
                </Mui.Grid>
            </Mui.DialogTitle>
            
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Mui.DialogContent>
                    <Mui.Grid container spacing={3} mb={3}>
                        <Mui.Grid item xs={12} sm={6} md={6}>
                            <FormInput
                                name={nameof<CreateDamageForm>("partId")}
                                type="select"
                                options={parts}
                                fullWidth
                                formInstance={form}
                                label={t("damageList.damageCreation.dialogs.createDamage.vehiclePart")}
                                value={`${form.watch("partId")}.${form.watch("positionId")}`}
                                onChange={(val) => {
                                    form.setValue("partId", parseInt(val?.split(".")?.[0]));
                                    form.setValue("positionId", parseInt(val?.split(".")?.[1]));
                                }}
                                validation={{
                                    required: t("damageList.damageCreation.dialogs.createDamage.required")
                                }}
                                error={!form.formState.errors.partId ? true : false}
                                helperText={form.formState.errors.partId?.message}
                            />
                        </Mui.Grid>
                        <Mui.Grid item xs={12} sm={6} md={6}>
                            <FormInput
                                name={nameof<CreateDamageForm>("severity")}
                                type="select"
                                options={severities}
                                formInstance={form}
                                fullWidth
                                label={t("damageList.damageCreation.dialogs.createDamage.severity")}
                                validation={{
                                    required: t("damageList.damageCreation.dialogs.createDamage.required")
                                }}
                            />
                        </Mui.Grid>
                    </Mui.Grid>

                    <FileUploader 
                        onUpload={(file: File) => onFileUpload(file, 1)}
                        onRemove={() => {console.log("remove")}}
                    />
                    <FileUploader 
                        onUpload={(file: File) => onFileUpload(file, 2)}
                        onRemove={() => {console.log("remove")}}
                    />
                </Mui.DialogContent>

                <Mui.DialogActions>
                    <Mui.Button variant="outlined" size="smallPlus" onClick={onClose}>
                        <Mui.Typography variant="h6" color="inherit">
                            {t("damageList.damageCreation.button.cancel")}
                        </Mui.Typography>
                    </Mui.Button>
                    <Mui.Button type="submit" size="smallPlus">
                        <Mui.Typography variant="h6" color="inherit">
                            {t("damageList.damageCreation.button.save")}
                        </Mui.Typography>
                    </Mui.Button>
                </Mui.DialogActions>
            </form>

        </Mui.Dialog>
    )
}
export default CreateDamage;